import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    Button,
    Grid,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    Breadcrumbs,
    Link,
    Stack,
    Paper,
    Fade,
    Slide
} from '@mui/material';
import {
    Add,
    Remove,
    Delete,
    ShoppingCartCheckout,
    ArrowBack,
    LocalShipping,
    Security,
    AttachMoney,
    ShoppingCart,
    RemoveShoppingCart,
    Home,
    KeyboardArrowRight
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import AppHeader from '../components/AppHeader';
import MobileDrawer from '../components/MobileDrawer';
import AppFooter from '../components/AppFooter';
import axios from 'axios';

const CartScreen = () => {
    const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [updating, setUpdating] = useState({});
    const [removing, setRemoving] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [cartSummary, setCartSummary] = useState({
        totalAmount: 0,
        itemCount: 0,
        currency: 'KWD'
    });
    const [user, setUser] = useState(null);

    const navigate = useNavigate();
    const t = translations[language];
    const theme = createAppTheme(true, language === 'ar');

    // Check authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
            navigate('/store'); // Redirect to products if not logged in
            return;
        }

        setUser(JSON.parse(userData));
        fetchCartItems();
    }, [navigate]);

    // Fetch cart items
    const fetchCartItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/store');
                return;
            }

            const response = await axios.get('https://dhahabjw.com/api/cart', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setCartItems(response.data.data.items || []);
                setCartSummary({
                    totalAmount: response.data.data.totalAmount || 0,
                    itemCount: response.data.data.itemCount || 0,
                    currency: response.data.data.currency || 'KWD'
                });
            } else {
                throw new Error(response.data.error || 'Failed to fetch cart');
            }
        } catch (error) {
            console.error('Error fetching cart:', error);

            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/store');
                return;
            }

            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to load cart items',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get localized name
    const getLocalizedName = (product) => {
        if (typeof product.name === 'object' && product.name !== null) {
            return product.name[language] || product.name.en || '';
        }
        return product.name || '';
    };

    // Update item quantity
    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1) return;

        try {
            setUpdating(prev => ({ ...prev, [itemId]: true }));
            const token = localStorage.getItem('token');

            const response = await axios.put(
                `https://dhahabjw.com/api/cart/update/${itemId}`,
                { quantity: newQuantity },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                // Update local state
                setCartItems(prevItems =>
                    prevItems.map(item =>
                        item.id === itemId
                            ? { ...item, quantity: newQuantity, totalPrice: item.unitPrice * newQuantity }
                            : item
                    )
                );

                // Recalculate total
                const updatedTotal = cartItems.reduce((total, item) => {
                    const quantity = item.id === itemId ? newQuantity : item.quantity;
                    return total + (item.unitPrice * quantity);
                }, 0);

                setCartSummary(prev => ({
                    ...prev,
                    totalAmount: parseFloat(updatedTotal.toFixed(3))
                }));

                // Update cart count in header
                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { itemCount: cartItems.length }
                }));

                setSnackbar({
                    open: true,
                    message: response.data.message || 'Quantity updated successfully',
                    severity: 'success'
                });
            } else {
                throw new Error(response.data.error || 'Failed to update quantity');
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to update quantity',
                severity: 'error'
            });
        } finally {
            setUpdating(prev => ({ ...prev, [itemId]: false }));
        }
    };

    // Remove item from cart
    const removeItem = async (itemId) => {
        try {
            setRemoving(prev => ({ ...prev, [itemId]: true }));
            const token = localStorage.getItem('token');

            const response = await axios.delete(
                `https://dhahabjw.com/api/cart/remove/${itemId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                // Remove item from local state
                setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));

                // Update summary
                const remainingItems = cartItems.filter(item => item.id !== itemId);
                const newTotal = remainingItems.reduce((total, item) => total + item.totalPrice, 0);

                setCartSummary(prev => ({
                    ...prev,
                    totalAmount: parseFloat(newTotal.toFixed(3)),
                    itemCount: remainingItems.length
                }));

                // Update cart count in header
                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { itemCount: remainingItems.length }
                }));

                setSnackbar({
                    open: true,
                    message: response.data.message || 'Item removed from cart',
                    severity: 'success'
                });
            } else {
                throw new Error(response.data.error || 'Failed to remove item');
            }
        } catch (error) {
            console.error('Error removing item:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to remove item',
                severity: 'error'
            });
        } finally {
            setRemoving(prev => ({ ...prev, [itemId]: false }));
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    // Handle checkout
    const handleCheckout = () => {
        if (cartItems.length === 0) return;

        // Navigate to checkout page (you'll need to create this)
        navigate('/checkout');
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
                    <AppHeader
                        language={language}
                        setLanguage={setLanguage}
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                        t={t}
                    />
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                        <Box textAlign="center">
                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                            <Typography color="text.secondary">{t.loading}</Typography>
                        </Box>
                    </Box>
                    <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
                <AppHeader
                    language={language}
                    setLanguage={setLanguage}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    t={t}
                />

                <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />

                <Container
                    maxWidth="xl"
                    sx={{
                        py: { xs: 2, sm: 3, md: 4 },
                        px: { xs: 1, sm: 2, md: 3 }
                    }}
                >
                    {cartItems.length === 0 ? (
                        // Empty Cart - Mobile Optimized
                        <Fade in={true}>
                            <Paper
                                sx={{
                                    p: { xs: 3, sm: 4, md: 6 },
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8), rgba(30, 30, 30, 0.8))',
                                    borderRadius: 3,
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    mx: { xs: 0, sm: 'auto' },
                                    maxWidth: { xs: '100%', sm: 600 }
                                }}
                            >
                                <RemoveShoppingCart
                                    sx={{
                                        fontSize: { xs: 60, sm: 70, md: 80 },
                                        color: 'text.secondary',
                                        mb: 2
                                    }}
                                />
                                <Typography
                                    variant="h5"
                                    color="text.primary"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>{t.emptyCart}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: 3,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        px: { xs: 1, sm: 2 }
                                    }}>{t.emptyCartMessage}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/store')}
                                    sx={{
                                        px: { xs: 3, sm: 4 },
                                        py: { xs: 1.5, sm: 2 },
                                        bgcolor: 'primary.main',
                                        color: 'black',
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        '&:hover': { bgcolor: 'primary.dark' }
                                    }}>{t.startShopping}</Button>
                            </Paper>
                        </Fade>
                    ) : (
                        // Cart Items - Mobile Optimized Layout
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            {/* Cart Items List */}
                            <Grid item xs={12} lg={8}>
                                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                    {cartItems.map((item, index) => (
                                        <Slide
                                            key={item.id}
                                            direction="up"
                                            in={true}
                                            timeout={300 + (index * 100)}
                                        >
                                            <Card sx={{ overflow: 'visible' }}>
                                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                    {/* Mobile Layout - Stack vertically on small screens */}
                                                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                        {/* Mobile: Product Image and Basic Info */}
                                                        <Box display="flex" gap={2} mb={2}>
                                                            <Box
                                                                sx={{
                                                                    position: 'relative',
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    cursor: 'pointer',
                                                                    minWidth: 80,
                                                                    height: 80,
                                                                    flexShrink: 0
                                                                }}
                                                                onClick={() => navigate(`/store/${item.product.id}`)}
                                                            >
                                                                <CardMedia
                                                                    component="img"
                                                                    height="80"
                                                                    image={item.product.images?.find(img => img.isPrimary)?.url || item.product.images?.[0]?.url}
                                                                    alt={getLocalizedName(item.product)}
                                                                    sx={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: 2,
                                                                        width: '100%',
                                                                        height: '100%'
                                                                    }}
                                                                />
                                                                {item.product.inventory?.quantity < 1 && (
                                                                    <Chip
                                                                        label={t.outOfStock || 'Out of Stock'}
                                                                        color="error"
                                                                        size="small"
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 4,
                                                                            left: 4,
                                                                            fontSize: '0.6rem',
                                                                            height: 20
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>

                                                            <Box flex={1} minWidth={0}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        mb: 0.5,
                                                                        cursor: 'pointer',
                                                                        fontSize: '1rem',
                                                                        lineHeight: 1.3,
                                                                        '&:hover': { color: 'primary.main' }
                                                                    }}
                                                                    onClick={() => navigate(`/store/${item.product.id}`)}
                                                                >
                                                                    {getLocalizedName(item.product)}
                                                                </Typography>

                                                                <Typography
                                                                    variant="h6"
                                                                    color="primary"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: '1.1rem'
                                                                    }}
                                                                >
                                                                    {item.totalPrice.toFixed(3)} {cartSummary.currency}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Mobile: Product Details */}
                                                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                                                            <Chip
                                                                label={item.product.metal}
                                                                size="small"
                                                                color="secondary"
                                                                sx={{ fontSize: '0.7rem', height: 24 }}
                                                            />
                                                            <Chip label={`${item.product.karat} ${t.karat}`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 24 }}
                                                            />
                                                            <Chip
                                                                label={`${item.product.weight}g`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 24 }}
                                                            />
                                                        </Stack>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mb: 2, fontSize: '0.85rem' }}
                                                        >{t.unitPrice}: {item.unitPrice.toFixed(3)} {cartSummary.currency}</Typography>

                                                        {/* Mobile: Remove Button */}
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            fullWidth
                                                            startIcon={removing[item.id] ? <CircularProgress size={16} /> : <Delete />}
                                                            onClick={() => handleDeleteClick(item)}
                                                            disabled={removing[item.id]}
                                                            sx={{
                                                                borderColor: 'error.main',
                                                                fontSize: '0.85rem',
                                                                py: 1,
                                                                '&:hover': {
                                                                    borderColor: 'error.dark',
                                                                    bgcolor: 'rgba(244, 67, 54, 0.1)'
                                                                }
                                                            }}
                                                        >{removing[item.id] ? t.removing : t.remove}</Button>
                                                    </Box>

                                                    {/* Desktop Layout - Hidden on mobile */}
                                                    <Grid
                                                        container
                                                        spacing={3}
                                                        alignItems="center"
                                                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                                                    >
                                                        {/* Product Image */}
                                                        <Grid item xs={12} sm={3}>
                                                            <Box
                                                                sx={{
                                                                    position: 'relative',
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => navigate(`/store/${item.product.id}`)}
                                                            >
                                                                <CardMedia
                                                                    component="img"
                                                                    height="120"
                                                                    image={item.product.images?.find(img => img.isPrimary)?.url || item.product.images?.[0]?.url}
                                                                    alt={getLocalizedName(item.product)}
                                                                    sx={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: 2,
                                                                        transition: 'transform 0.3s ease',
                                                                        '&:hover': {
                                                                            transform: 'scale(1.05)'
                                                                        }
                                                                    }}
                                                                />
                                                                {item.product.inventory?.quantity < 1 && (
                                                                    <Chip
                                                                        label={t.outOfStock || 'Out of Stock'}
                                                                        color="error"
                                                                        size="small"
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 8,
                                                                            left: 8,
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Grid>

                                                        {/* Product Details */}
                                                        <Grid item xs={12} sm={5}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    mb: 1,
                                                                    cursor: 'pointer',
                                                                    '&:hover': { color: 'primary.main' }
                                                                }}
                                                                onClick={() => navigate(`/store/${item.product.id}`)}
                                                            >
                                                                {getLocalizedName(item.product)}
                                                            </Typography>

                                                            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                                                                <Chip
                                                                    label={item.product.metal}
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                                <Chip
                                                                    label={`${item.product.karat} Karat`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                                <Chip
                                                                    label={`${item.product.weight}g`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </Stack>

                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ mb: 1 }}
                                                            >
                                                                {t.unitPrice || 'Unit Price'}: {item.unitPrice.toFixed(3)} {cartSummary.currency}
                                                            </Typography>

                                                            <Typography
                                                                variant="h6"
                                                                color="primary"
                                                                sx={{ fontWeight: 600 }}
                                                            >{t.total}: {item.totalPrice.toFixed(3)} {cartSummary.currency}</Typography>
                                                        </Grid>

                                                        {/* Quantity Controls */}
                                                        <Grid item xs={12} sm={4}>
                                                            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={removing[item.id] ? <CircularProgress size={16} /> : <Delete />}
                                                                    onClick={() => handleDeleteClick(item)}
                                                                    disabled={removing[item.id]}
                                                                    sx={{
                                                                        minWidth: '120px',
                                                                        borderColor: 'error.main',
                                                                        '&:hover': {
                                                                            borderColor: 'error.dark',
                                                                            bgcolor: 'rgba(244, 67, 54, 0.1)'
                                                                        }
                                                                    }}
                                                                >
                                                                    {removing[item.id] ? 'Removing...' : (t.remove || 'Remove')}
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Slide>
                                    ))}
                                </Stack>
                            </Grid>

                            {/* Cart Summary - Mobile Optimized */}
                            <Grid item xs={12} lg={4}>
                                <Box
                                    position={{ xs: 'static', lg: 'sticky' }}
                                    top={100}
                                    sx={{ mt: { xs: 2, lg: 0 } }}
                                >
                                    <Card>
                                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                                }}
                                            >{t.orderSummary}</Typography>

                                            <Stack spacing={2} sx={{ mb: 3 }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography
                                                        color="text.secondary"
                                                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                                    >{t.subtotal} ({cartSummary.itemCount} {cartSummary.itemCount === 1 ? t.item : t.items})</Typography>
                                                    <Typography
                                                        fontWeight={500}
                                                        sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                                                    >
                                                        {cartSummary.totalAmount.toFixed(3)} {cartSummary.currency}
                                                    </Typography>
                                                </Box>
                                                <Divider />

                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={600}
                                                        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                                                    >{t.total}</Typography>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={600}
                                                        color="primary"
                                                        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                                                    >
                                                        {cartSummary.totalAmount.toFixed(3)} {cartSummary.currency}
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            <Button
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                startIcon={<ShoppingCartCheckout />}
                                                onClick={handleCheckout}
                                                sx={{
                                                    py: { xs: 1.5, sm: 2 },
                                                    mb: 2,
                                                    bgcolor: 'primary.main',
                                                    color: 'black',
                                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                                    '&:hover': { bgcolor: 'primary.dark' }
                                                }}
                                            >{t.proceedToCheckout}</Button>

                                            {/* Security & Shipping Info */}
                                            <Stack
                                                spacing={1.5}
                                                sx={{
                                                    mt: 3,
                                                    pt: 3,
                                                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                                                }}
                                            >
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <Security sx={{ color: 'success.main', fontSize: { xs: 18, sm: 20 } }} />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                    >{t.secureCheckout}</Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Container>

                {/* Delete Confirmation Dialog - Mobile Optimized */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            mx: { xs: 2, sm: 'auto' },
                            width: { xs: 'calc(100% - 32px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{t.confirmRemove}</DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>{t.confirmRemoveMessage}</Typography>
                        {itemToDelete && (
                            <Box sx={{
                                mt: 2,
                                p: { xs: 1.5, sm: 2 },
                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: 1
                            }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                >
                                    {getLocalizedName(itemToDelete.product)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    {itemToDelete.quantity} × {itemToDelete.unitPrice.toFixed(3)} {cartSummary.currency}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <Button
                            onClick={() => setDeleteDialogOpen(false)}
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }}>{t.cancel}</Button>
                        <Button
                            onClick={() => itemToDelete && removeItem(itemToDelete.id)}
                            color="error"
                            variant="contained"
                            disabled={removing[itemToDelete?.id]}
                            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{removing[itemToDelete?.id] ? t.removing : t.remove}</Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    sx={{
                        bottom: { xs: 80, sm: 24 }, // Higher on mobile to avoid navigation
                        '& .MuiSnackbarContent-root': {
                            fontSize: { xs: '0.85rem', sm: '0.875rem' }
                        }
                    }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{
                            width: '100%',
                            fontSize: { xs: '0.85rem', sm: '0.875rem' }
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <AppFooter t={t} />
            </Box>
        </ThemeProvider>
    );
};

export default CartScreen;